import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase.config";
import dataCache from "../../services/DataCache";
import remoteQuizService from "../../services/RemoteQuizService";
import { calculatePoliticalAlignmentCategory } from "../../utils/derivedFields";
import {
  Groups,
  Balance,
  Person,
  AccountBalance,
  Handshake,
  Nature,
  Engineering,
  Psychology,
  AutoAwesome,
} from "@mui/icons-material";

const calculateBig3Traits = (quizData) => {
  const traits = {
    personality: null,
    political: null,
    thinking: null,
  };

  quizData.forEach((quiz) => {
    if (quiz.quizType === "personality-check") {
      const introversionResults = quiz.results.filter(
        (r) => r.field === "introversion"
      );
      if (introversionResults.length > 0) {
        const averagePercentage = Math.round(
          introversionResults.reduce((sum, r) => sum + r.percentage1, 0) /
            introversionResults.length
        );
        if (averagePercentage <= 33) {
          traits.personality = { label: "Extrovert", icon: Groups };
        } else if (averagePercentage <= 66) {
          traits.personality = { label: "Ambivert", icon: Balance };
        } else {
          traits.personality = { label: "Introvert", icon: Person };
        }
      }

      const intuitionResults = quiz.results.filter(
        (r) => r.field === "intuition"
      );
      if (intuitionResults.length > 0) {
        const averagePercentage = Math.round(
          intuitionResults.reduce((sum, r) => sum + r.percentage1, 0) /
            intuitionResults.length
        );
        if (averagePercentage <= 33) {
          traits.thinking = { label: "Concrete", icon: Engineering };
        } else if (averagePercentage <= 66) {
          traits.thinking = { label: "Balanced", icon: Psychology };
        } else {
          traits.thinking = { label: "Abstract", icon: AutoAwesome };
        }
      }
    }

    if (quiz.quizType === "political-alignment") {
      const politicalFields = [
        "socialLib",
        "econLib",
        "globalism",
        "conservation",
        "public",
        "socialSupport",
      ];
      const politicalResults = quiz.results.filter((r) =>
        politicalFields.includes(r.field)
      );

      if (politicalResults.length > 0) {
        const fieldScores = {};
        politicalResults.forEach((result) => {
          if (!fieldScores[result.field]) {
            fieldScores[result.field] = [];
          }
          fieldScores[result.field].push(result.percentage1);
        });

        const processedScores = {};
        Object.entries(fieldScores).forEach(([field, percentages]) => {
          const average =
            percentages.reduce((sum, percent) => sum + percent, 0) /
            percentages.length;
          processedScores[field] = Math.round(average);
        });

        const alignment = calculatePoliticalAlignmentCategory(processedScores);

        if (alignment === "Liberal") {
          traits.political = { label: "Liberal", icon: Nature };
        } else if (alignment === "Conservative") {
          traits.political = { label: "Conservative", icon: AccountBalance };
        } else {
          traits.political = { label: "Moderate", icon: Handshake };
        }
      }
    }
  });

  return traits;
};

export const useBig3Traits = (userId) => {
  const [big3, setBig3] = useState(null);
  const [big3Loading, setBig3Loading] = useState(false);

  useEffect(() => {
    const fetchBig3 = async () => {
      if (!userId) return;
      setBig3Loading(true);
      try {
        let userProfile = dataCache.getUserProfile(userId);
        if (!userProfile) {
          const profileRef = doc(db, "public-profiles", userId);
          const profileSnap = await getDoc(profileRef);
          if (!profileSnap.exists()) {
            setBig3(null);
            return;
          }
          userProfile = profileSnap.data();
        }

        const quizData = [];
        // Big 3 only uses personality-check and political-alignment quizzes
        const big3QuizTypes = ["personality-check", "political-alignment"];

        for (const quizType of big3QuizTypes) {
          const config = await remoteQuizService.getQuizConfig(quizType);
          if (!config) continue;

          const quizId = userProfile[config.databaseField];
          if (quizId) {
            try {
              const quizCacheKey = `quiz-data-${quizId}`;
              let quizDocData = dataCache.get(quizCacheKey);
              if (!quizDocData) {
                const quizDocRef = doc(db, "quiz-answers", quizId);
                const quizDoc = await getDoc(quizDocRef);
                if (quizDoc.exists()) {
                  quizDocData = quizDoc.data();
                  dataCache.set(quizCacheKey, quizDocData, 10 * 60 * 1000);
                }
              }
              if (quizDocData && quizDocData.status === "completed") {
                const quizAnswers = quizDocData.answers;
                const results = Object.values(quizAnswers).map((answer) => ({
                  field: answer.field,
                  percentage1: answer.percentage,
                }));
                quizData.push({ quizType, results });
              }
            } catch (_) {}
          }
        }

        setBig3(calculateBig3Traits(quizData));
      } finally {
        setBig3Loading(false);
      }
    };

    fetchBig3();
  }, [userId]);

  return { big3, big3Loading };
};
